// The suite every implementation of ICD-S05 passes.
//
// The five checks worth writing first are here because each is
// a failure that looks exactly like success until one specific day of the
// year: daylight saving in both directions (7, 20), catch-up after an outage
// (11), idempotency per job kind (12) and a lease that expires (15).
//
// NO SPORT IN SIGHT, and that is the discipline rather than an accident. The
// fixture supplies a `testball` calendar that groups games by the week number
// the source gave them. If a check here passed only against football's
// calendar it would be testing football.
//
// EVERY CHECK IS PROVED AGAINST A BREAK. `conformance.test.ts` runs each one
// against an implementation built to fail exactly it; a check nobody has
// watched fail is a check nobody knows works.

import { LeagueSettings, type JobKindId, type ScheduledJobShape } from '@fsp/domain';
import type { Game, LineupWindow, Period } from '@fsp/sports-core';
import { recordingBus, type JobBus } from './bus';
import { dailyPlanAt } from './due';
import { standardHandlers, withCalendar } from './handlers';
import { jobId, PLANS_NOTHING, planJobs } from './plan';
import type { ClockLeague, ClockService, ClockSport, JobHandler } from './types';
import { partsIn } from './zone';
import { SETTINGS_SCHEMA_VERSION } from '@fsp/domain';

/**
 * The numbers the shared suite runs. 33 and 48 are SQL-only
 * (`scripts/clock/verify.ts` runs them), so the list has gaps where they
 * would be; 37, 38 and 40 were the Phase 0 sweep's pass over leagues, which
 * Phase 1 replaced (42–47), and their numbers are not reused.
 */
export const CLOCK_CHECK_IDS: readonly number[] = [
  ...Array.from({ length: 32 }, (_, i) => i + 1),
  34,
  35,
  36,
  39,
  41,
  42,
  43,
  44,
  45,
  46,
  47,
  49,
  50,
  51,
];
export const CLOCK_CHECKS = CLOCK_CHECK_IDS.length;

export interface CheckResult {
  n: number;
  name: string;
  ok: boolean;
  detail?: string;
}

/** What a factory is handed. Every check builds the one it needs. */
export interface ConformanceWorld {
  leagues: ClockLeague[];
  games: Game[];
  slots: {
    leagueId: string;
    teamId: string;
    windowId: string;
    slot: string;
    lockedAt: string | null;
    /** The pro team of the player in the slot, for `per-player-game`. */
    club: string;
    /** Zero when unset. Check 30 fills three spots of one slot. */
    slotIndex?: number;
    /**
     * One player per club when unset, which is all a lock needs. Check 30
     * names them, and null is an empty cell — what the local seeder writes.
     */
    playerId?: string | null;
  }[];
  /** Who holds whom — `roster_players`. Check 30: a player traded away is not carried. */
  rosters?: { teamId: string; playerId: string }[];
  /**
   * Stats projection runs, as the ingest ledger records them: when one
   * finished, for which sport, and how many lines it wrote. Check 31.
   */
  landings?: { sport: string; at: string; rows: number }[];
  definitions: Record<string, ClockSport>;
  /** Ids in `games` the simulated world wrote — `games.source = 'sim'` in SQL. Check 28. */
  simGames?: string[];
  handlers?: Map<JobKindId, JobHandler>;
  /**
   * Where a sweep sends each league's work. Unset, the sweep runs it itself;
   * the checks about the sending (42, 44, 45) give one that keeps what it
   * was sent and consume it by hand.
   */
  bus?: JobBus;
  /**
   * Called after the suite has changed `games` in place.
   *
   * The world moves — that is the whole of checks 17 and 18 — and an
   * implementation backed by a table needs telling so it can re-seed. One
   * backed by the array itself needs nothing and leaves this unset. An
   * implementation that cached its schedule at construction fails both checks,
   * which is the other thing this hook is for.
   */
  onGamesChanged?: () => Promise<void>;
  /**
   * A team's lineup rows in one window, read back from the implementation's
   * storage. Set by the factory, like `onGamesChanged`: a carry is a write no
   * `ClockService` method returns, and check 30 has to see what it wrote.
   */
  readSlots?: (teamId: string, windowId: string) => Promise<ConformanceSlotRow[]>;
}

/** One lineup row as check 30 reads it back. */
export interface ConformanceSlotRow {
  slot: string;
  slotIndex: number;
  playerId: string | null;
  setBy: string | null;
  lockedAt: string | null;
}

/** An implementation, built over a world. Called once per check. */
export type ClockFactory = (world: ConformanceWorld) => ClockService | Promise<ClockService>;

const CHI = 'America/Chicago';
const DAY = 24 * 60 * 60 * 1000;

// ── The fixture sport ─────────────────────────────────────────────────────

/**
 * One period per source week, one lineup window covering it.
 *
 * Deliberately simpler than football's: a period runs from three days before
 * its first kickoff (or the previous period's end) to twelve hours after its
 * last, and never past the next period's first kickoff. That is enough shape
 * for every check here and none of football's particulars.
 */
export const testball: ClockSport = {
  calendar: {
    periods(season, _lineupPeriod, games): readonly Period[] {
      const byWeek = new Map<number, Game[]>();
      for (const g of games) {
        if (g.season !== season || g.periodHint === undefined) continue;
        // A canceled game still says the week EXISTED, so the period is built
        // and its window is simply empty. Dropping it here would delete the
        // week instead, which is how check 8 first passed against a break: the
        // fixture had no empty window in it to get wrong.
        byWeek.set(g.periodHint, [...(byWeek.get(g.periodHint) ?? []), g]);
      }
      const weeks = [...byWeek.keys()].sort((a, b) => a - b);
      const all = new Set(games.flatMap((g) => [g.homeTeam, g.awayTeam]));
      const out: Period[] = [];
      weeks.forEach((week, i) => {
        const mine = byWeek.get(week)!;
        const live = mine.filter((g) => g.status !== 'canceled');
        const starts = (live.length > 0 ? live : mine)
          .map((g) => Date.parse(g.start))
          .sort((a, b) => a - b);
        const first = starts[0]!;
        const last = starts[starts.length - 1]!;
        const nextWeek = weeks[i + 1];
        const nextFirst = nextWeek
          ? Math.min(...byWeek.get(nextWeek)!.map((g) => Date.parse(g.start)))
          : null;
        const start = i === 0 ? first - 3 * DAY : Date.parse(out[i - 1]!.end);
        const end = nextFirst === null ? last + DAY / 2 : Math.min(last + DAY / 2, nextFirst);
        const playing = new Set(live.flatMap((g) => [g.homeTeam, g.awayTeam]));
        const windows: LineupWindow[] = [
          { id: `${season}-w${String(week).padStart(2, '0')}`, start: iso(start), end: iso(end) },
        ];
        out.push({
          season,
          index: week,
          label: `Week ${week}`,
          start: iso(start),
          end: iso(end),
          windows,
          flags: {
            playoffEligible: true,
            proBye: [...all].filter((t) => !playing.has(t)).sort(),
          },
        });
      });
      return out;
    },
    defaultRegularSeasonPeriods: () => ({ first: 1, last: 14 }),
    defaultPlayoffPeriods: () => [15, 16, 17],
    isInSeason: () => true,
  },
};

// ── Fixture helpers ───────────────────────────────────────────────────────

const iso = (at: number): string => new Date(at).toISOString();

export function gameAt(id: string, start: string, week: number, home = 'HAW', away = 'OWL'): Game {
  return {
    id,
    sport: 'football',
    season: 2026,
    start,
    homeTeam: home,
    awayTeam: away,
    status: 'scheduled',
    periodHint: week,
  };
}

/**
 * Real settings with no sport package in sight — the same argument
 * `packages/league`'s fixture makes, and put through `LeagueSettings.parse`
 * for the same reason: a fixture that would not parse hides a store that
 * stores nonsense.
 */
export function settingsFor(overrides: {
  lockRule?: 'per-player-game' | 'first-game' | 'fixed-time';
  fixedLockTime?: string | null;
  timeZone?: string;
  waivers?: { schedule: 'daily' | 'weekly' | 'continuous'; time: string; weekday?: number | null };
  /** `faab` unless a check asks; `none` is a league with no waivers (check 32). */
  waiverSystem?: 'faab' | 'none';
  deadlinePeriod?: number | null;
  draftScheduledAt?: string | null;
  firstPeriod?: number;
  lastPeriod?: number;
}): LeagueSettings {
  return LeagueSettings.parse({
    schemaVersion: SETTINGS_SCHEMA_VERSION,
    sport: 'football',
    season: 2026,
    identity: { name: 'Conformance', teamCount: 10, timeZone: overrides.timeZone ?? CHI },
    format: { id: 'h2h-points', elimination: null },
    scoring: { units: [{ stat: 'points', per: 1, points: 1, positions: null }] },
    categories: null,
    roster: {
      slots: [
        { slot: 'START', count: 9 },
        { slot: 'BENCH', count: 6 },
      ],
    },
    lineup: {
      period: 'weekly',
      lockRule: overrides.lockRule ?? 'per-player-game',
      fixedLockTime: overrides.fixedLockTime ?? null,
    },
    schedule: {
      regularSeason: {
        firstPeriod: overrides.firstPeriod ?? 1,
        lastPeriod: overrides.lastPeriod ?? 14,
      },
    },
    playoffs: { teamCount: 6, periods: [15, 16, 17], byes: 2 },
    tiebreakers: { standings: ['head-to-head', 'points-for'] },
    waivers: {
      system: overrides.waiverSystem ?? 'faab',
      faab: overrides.waiverSystem === 'none' ? null : { budget: 100 },
      processing: overrides.waivers ?? { schedule: 'weekly', time: '03:00', weekday: 3 },
    },
    acquisitions: {},
    trades: {
      review: 'commissioner',
      deadlinePeriod: overrides.deadlinePeriod === undefined ? null : overrides.deadlinePeriod,
    },
    draft: {
      type: 'snake',
      scheduledAt: overrides.draftScheduledAt ?? null,
      rounds: 15,
      pickSeconds: 90,
    },
    keepers: { mode: 'redraft' },
    governance: {},
    fun: {},
  });
}

export function leagueFor(
  id: string,
  settings: LeagueSettings,
  status = 'in-season',
  seeded: string | null = null,
  frozenAt: string | null = null,
): ClockLeague {
  return { id, sport: 'football', season: 2026, status, settings, seeded, frozenAt };
}

/**
 * Three weeks in September 2026, with a Thursday, two Sundays and a Monday.
 *
 * Week 1 Thu 2026-09-10 20:15 America/New_York is 2026-09-11T00:15Z.
 */
export function threeWeeks(): Game[] {
  const weeks = [
    [
      '2026-09-11T00:15:00.000Z',
      '2026-09-13T17:00:00.000Z',
      '2026-09-13T20:25:00.000Z',
      '2026-09-15T00:15:00.000Z',
    ],
    [
      '2026-09-18T00:15:00.000Z',
      '2026-09-20T17:00:00.000Z',
      '2026-09-20T20:25:00.000Z',
      '2026-09-22T00:15:00.000Z',
    ],
    [
      '2026-09-25T00:15:00.000Z',
      '2026-09-27T17:00:00.000Z',
      '2026-09-27T20:25:00.000Z',
      '2026-09-29T00:15:00.000Z',
    ],
  ];
  const teams = [
    ['HAW', 'OWL'],
    ['FOX', 'ELK'],
    ['RAM', 'BAT'],
    ['CAT', 'DOG'],
  ];
  return weeks.flatMap((starts, w) =>
    starts.map((start, i) =>
      gameAt(`g-w${w + 1}-${i}`, start, w + 1, teams[i]![0]!, teams[i]![1]!),
    ),
  );
}

export function worldFor(
  leagues: ClockLeague[],
  games: Game[],
  extra: Partial<ConformanceWorld> = {},
): ConformanceWorld {
  return { leagues, games, slots: [], definitions: { football: testball }, ...extra };
}

class Failed extends Error {}
function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Failed(message);
}

function unwrap<T>(result: { ok: true; value: T } | { ok: false; message: string }): T {
  assert(result.ok, `refused: ${result.ok === false ? result.message : ''}`);
  return result.value;
}

/** Local `HH:MM` of an instant, for the daylight-saving checks. */
function localTime(zone: string, at: string): string {
  const p = partsIn(zone, new Date(at));
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

const ALL_KINDS = new Set<JobKindId>([
  'period.open',
  'lineup.lock',
  'matchup.score',
  'matchup.finalize',
  'stats.correct',
  'waivers.process',
  'trades.deadline',
  'draft.start',
  'period.close',
  'season.rollover',
]);

/** Everything the planner would emit, whatever a deployment has handlers for. */
function planAll(
  league: ClockLeague,
  periods: readonly (Period & {
    windows: readonly (LineupWindow & { firstKickoff: string | null })[];
  })[],
  games: readonly Game[],
  since: Date,
  now: Date,
): ScheduledJobShape[] {
  return planJobs({
    leagueId: league.id,
    settings: league.settings,
    periods: periods.map((p) => ({ ...p, kind: 'regular' as const })),
    games,
    since,
    now,
    kinds: ALL_KINDS,
  });
}

export async function runClockConformance(make: ClockFactory): Promise<CheckResult[]> {
  const results: CheckResult[] = [];

  async function check(n: number, name: string, body: () => Promise<void>): Promise<void> {
    try {
      await body();
      results.push({ n, name, ok: true });
    } catch (error) {
      results.push({
        n,
        name,
        ok: false,
        detail: error instanceof Error ? error.message : String(error),
      });
    }
  }

  const NOW = new Date('2026-09-09T12:00:00.000Z');

  // ── The calendar ────────────────────────────────────────────────────────

  await check(1, 'periods tile without gaps and hold every game', async () => {
    const league = leagueFor('lg-1', settingsFor({}));
    const games = threeWeeks();
    const clock = await make(worldFor([league], games));
    unwrap(await clock.materialize('lg-1', NOW));
    const periods = unwrap(await clock.forLeague('lg-1'));
    assert(periods.length === 3, `expected 3 periods, got ${periods.length}`);
    for (let i = 1; i < periods.length; i++) {
      assert(
        periods[i]!.start === periods[i - 1]!.end,
        `period ${i + 1} starts at ${periods[i]!.start}, previous ended ${periods[i - 1]!.end}`,
      );
    }
    for (const game of games) {
      const at = Date.parse(game.start);
      const holding = periods.filter((p) => at >= Date.parse(p.start) && at < Date.parse(p.end));
      assert(holding.length === 1, `${game.id} falls in ${holding.length} periods`);
    }
  });

  await check(2, 'windows are ordered and cover their period', async () => {
    const league = leagueFor('lg-1', settingsFor({}));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    for (const period of unwrap(await clock.forLeague('lg-1'))) {
      assert(period.windows.length >= 1, `period ${period.index} has no window`);
      assert(
        period.windows[0]!.start === period.start,
        'first window does not start with the period',
      );
      const last = period.windows[period.windows.length - 1]!;
      assert(last.end === period.end, 'last window does not end with the period');
      for (let i = 1; i < period.windows.length; i++) {
        assert(period.windows[i]!.start === period.windows[i - 1]!.end, 'windows do not tile');
      }
    }
  });

  await check(3, 'a team with no game this period is on the bye list, and only then', async () => {
    const league = leagueFor('lg-1', settingsFor({}));
    const games = threeWeeks().filter((g) => !(g.periodHint === 2 && g.homeTeam === 'CAT'));
    const clock = await make(worldFor([league], games));
    unwrap(await clock.materialize('lg-1', NOW));
    const periods = unwrap(await clock.forLeague('lg-1'));
    const week2 = periods.find((p) => p.index === 2)!;
    assert(
      week2.flags.proBye.includes('CAT'),
      'CAT has no game in week 2 and is not on the bye list',
    );
    assert(
      week2.flags.proBye.includes('DOG'),
      'DOG has no game in week 2 and is not on the bye list',
    );
    const week1 = periods.find((p) => p.index === 1)!;
    assert(
      week1.flags.proBye.length === 0,
      `week 1 should have no byes, has ${week1.flags.proBye.join()}`,
    );
  });

  // ── The locks ───────────────────────────────────────────────────────────

  await check(4, 'per-player-game: the window has no single lock instant', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'per-player-game' }));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const view = unwrap(await clock.stateOf('lg-1', new Date('2026-09-13T18:00:00.000Z')));
    const window = view.current!.windows[0]!;
    assert(window.lockAt === null, `expected no window lock, got ${window.lockAt}`);
    assert(window.locked === false, 'a window with no lock instant cannot be locked');
    assert(
      window.firstKickoff === '2026-09-11T00:15:00.000Z',
      `first kickoff is ${window.firstKickoff}`,
    );
  });

  await check(5, 'first-game: the window locks at its first kickoff', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const before = unwrap(await clock.stateOf('lg-1', new Date('2026-09-11T00:14:59.000Z')));
    const after = unwrap(await clock.stateOf('lg-1', new Date('2026-09-11T00:15:00.000Z')));
    assert(before.current!.windows[0]!.lockAt === '2026-09-11T00:15:00.000Z', 'wrong lock instant');
    assert(before.current!.windows[0]!.locked === false, 'locked a second early');
    assert(after.current!.windows[0]!.locked === true, 'not locked at the instant itself');
  });

  await check(6, 'fixed-time: the lock is that wall time in the league zone', async () => {
    const league = leagueFor(
      'lg-1',
      settingsFor({ lockRule: 'fixed-time', fixedLockTime: '12:00', timeZone: CHI }),
    );
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const view = unwrap(await clock.stateOf('lg-1', new Date('2026-09-12T00:00:00.000Z')));
    const lockAt = view.current!.windows[0]!.lockAt!;
    assert(lockAt !== null, 'a fixed-time league has no lock instant');
    assert(
      localTime(CHI, lockAt) === '12:00',
      `lock renders as ${localTime(CHI, lockAt)} in ${CHI}`,
    );
  });

  await check(7, 'fixed-time holds across daylight saving, both directions', async () => {
    // Spring forward is 2026-03-08 and fall back is 2026-11-01 in the US. A
    // lock stated as 12:00 must read as 12:00 on both sides of each, which it
    // cannot if an offset was stored instead of a zone.
    const league = leagueFor(
      'lg-dst',
      settingsFor({ lockRule: 'fixed-time', fixedLockTime: '12:00', timeZone: CHI }),
    );
    const games = [
      gameAt('g-mar-before', '2026-03-07T18:00:00.000Z', 1),
      gameAt('g-mar-after', '2026-03-14T18:00:00.000Z', 2),
      gameAt('g-nov-before', '2026-10-31T18:00:00.000Z', 3),
      gameAt('g-nov-after', '2026-11-07T18:00:00.000Z', 4),
    ];
    const clock = await make(worldFor([league], games));
    unwrap(await clock.materialize('lg-dst', NOW));
    const periods = unwrap(await clock.forLeague('lg-dst'));
    assert(periods.length === 4, `expected 4 periods, got ${periods.length}`);
    const view = unwrap(await clock.stateOf('lg-dst', new Date('2026-03-01T00:00:00.000Z')));
    // `stateOf` gives the current and next period only, so read the locks from
    // the stored calendar through a second view at each period's own moment.
    for (const period of periods) {
      const at = new Date(Date.parse(period.start) + 60_000);
      const seen = unwrap(await clock.stateOf('lg-dst', at));
      const window = seen.current?.windows[0];
      assert(window, `no current window at ${at.toISOString()}`);
      assert(window!.lockAt !== null, `period ${period.index} has no lock`);
      const local = localTime(CHI, window!.lockAt!);
      assert(local === '12:00', `period ${period.index} locks at ${local} local, not 12:00`);
    }
    assert(view.timeZone === CHI, 'the view does not name its zone');
  });

  await check(8, 'a window with no game never locks', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    // Week 2 exists — the source still lists its fixture — but the one game in
    // it was called off, so there is nothing to lock against. A bye week, a
    // league that started mid-season and an unpublished week all land here.
    const games = [
      ...threeWeeks().filter((g) => g.periodHint !== 2),
      { ...gameAt('g-w2-off', '2026-09-20T17:00:00.000Z', 2), status: 'canceled' as const },
    ];
    const clock = await make(worldFor([league], games));
    unwrap(await clock.materialize('lg-1', NOW));
    const periods = unwrap(await clock.forLeague('lg-1'));
    const week2 = periods.find((p) => p.index === 2);
    assert(week2, 'the fixture lost the empty week, so nothing was proved');
    assert(week2!.windows.length >= 1, 'an empty period still needs a window');
    assert(
      week2!.windows[0]!.firstKickoff === null,
      `the empty window has a kickoff: ${week2!.windows[0]!.firstKickoff}`,
    );
    const inside = new Date(Date.parse(week2!.start) + 60_000);
    const view = unwrap(await clock.stateOf('lg-1', inside));
    const seen = view.current!.windows[0]!;
    assert(seen.lockAt === null, `an empty window has a lock at ${seen.lockAt}`);
    assert(seen.locked === false, 'an empty window reports itself locked');
  });

  await check(9, 'reading the clock writes nothing', async () => {
    const league = leagueFor('lg-1', settingsFor({}));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const at = new Date('2026-09-14T00:00:00.000Z');
    const first = unwrap(await clock.stateOf('lg-1', at));
    const second = unwrap(await clock.stateOf('lg-1', at));
    assert(JSON.stringify(first) === JSON.stringify(second), 'two reads of one instant disagree');
    const after = unwrap(await clock.due('lg-1', new Date(at.getTime() - DAY), at));
    const again = unwrap(await clock.due('lg-1', new Date(at.getTime() - DAY), at));
    assert(after.length === again.length, 'reading what is due changed what is due');
  });

  // ── The tick ────────────────────────────────────────────────────────────

  await check(10, 'a job is not due one millisecond early', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const lock = new Date('2026-09-11T00:15:00.000Z');
    const early = unwrap(
      await clock.due('lg-1', new Date(lock.getTime() - DAY), new Date(lock.getTime() - 1)),
    );
    assert(
      !early.some((j) => j.kind === 'lineup.lock' && j.dueAt === lock.toISOString()),
      'the lock job was due before its instant',
    );
    const onTime = unwrap(await clock.due('lg-1', new Date(lock.getTime() - DAY), lock));
    assert(
      onTime.some((j) => j.kind === 'lineup.lock' && j.dueAt === lock.toISOString()),
      'the lock job was not due at its instant',
    );
    // And `run` refuses even if somebody hands it the job anyway.
    const job = onTime.find((j) => j.kind === 'lineup.lock')!;
    const refused = await clock.run(job, new Date(lock.getTime() - 1));
    assert(!refused.ok, 'a job ran before it was due');
  });

  await check(11, 'a sweep that wakes late runs what it slept through', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const lock = Date.parse('2026-09-11T00:15:00.000Z');
    const late = new Date(lock + 10 * 60 * 1000);
    const due = unwrap(await clock.due('lg-1', new Date(lock - DAY), late));
    const found = due.find((j) => j.kind === 'lineup.lock' && Date.parse(j.dueAt) === lock);
    assert(found, 'ten minutes late and the job had been skipped');
    const outcome = unwrap(await clock.run(found!, late));
    assert(outcome.dueAt === new Date(lock).toISOString(), 'the outcome forgot when it was due');
    assert(outcome.lateBy === 600, `lateBy is ${outcome.lateBy}, expected 600`);
  });

  await check(12, 'every kind, run twice, leaves what running it once left', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }), 'in-season');
    const slots = [1, 2, 3].map((i) => ({
      leagueId: 'lg-1',
      teamId: 't-1',
      windowId: '2026-w01',
      slot: `START-${i}`,
      lockedAt: null,
      club: 'HAW',
    }));
    const clock = await make(worldFor([league], threeWeeks(), { slots }));
    unwrap(await clock.materialize('lg-1', NOW));
    const at = new Date('2026-09-11T00:20:00.000Z');
    const due = unwrap(
      await clock.due('lg-1', new Date(Date.parse('2026-09-01T00:00:00.000Z')), at),
    );
    assert(due.length > 0, 'nothing was due, so nothing was proved');
    for (const job of due) {
      const first = unwrap(await clock.run(job, at));
      const second = unwrap(await clock.run(job, at));
      assert(first.attempt === 1, `first attempt is numbered ${first.attempt}`);
      assert(second.attempt === 2, `second attempt is numbered ${second.attempt}`);
      // The second run must find nothing left to do. That is the whole claim:
      // not that it is refused, but that it changes nothing.
      assert(
        second.status === 'idle',
        `${job.kind} run twice reported ${second.status}, so the second run did something`,
      );
    }
  });

  await check(13, 'due work comes back in due order, and ties in the fixed order', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const at = new Date('2026-09-30T00:00:00.000Z');
    const due = unwrap(await clock.due('lg-1', new Date('2026-09-01T00:00:00.000Z'), at));
    for (let i = 1; i < due.length; i++) {
      assert(
        Date.parse(due[i]!.dueAt) >= Date.parse(due[i - 1]!.dueAt),
        `job ${i} is due before the one in front of it`,
      );
    }
    // At one instant, opening a period comes before locking a lineup in it.
    const periods = unwrap(await clock.forLeague('lg-1'));
    const planned = planAll(
      league,
      periods as never,
      threeWeeks(),
      new Date('2026-09-01T00:00:00.000Z'),
      at,
    );
    const sameInstant = new Map<string, JobKindId[]>();
    for (const job of planned) {
      sameInstant.set(job.dueAt, [...(sameInstant.get(job.dueAt) ?? []), job.kind]);
    }
    for (const [instant, kinds] of sameInstant) {
      if (!kinds.includes('period.open') || !kinds.includes('lineup.lock')) continue;
      assert(
        kinds.indexOf('period.open') < kinds.indexOf('lineup.lock'),
        `at ${instant} a lock is planned before the period opens`,
      );
    }
  });

  await check(14, 'a job that throws is recorded failed and stays eligible', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const handlers = new Map<JobKindId, JobHandler>([
      [
        'lineup.lock',
        {
          kind: 'lineup.lock',
          run: async () => {
            throw new Error('D1 timed out');
          },
        },
      ],
    ]);
    const clock = await make(worldFor([league], threeWeeks(), { handlers }));
    unwrap(await clock.materialize('lg-1', NOW));
    const at = new Date('2026-09-11T00:20:00.000Z');
    const since = new Date('2026-09-01T00:00:00.000Z');
    const due = unwrap(await clock.due('lg-1', since, at));
    const job = due.find((j) => j.kind === 'lineup.lock');
    assert(job, 'no lock job to fail');
    const outcome = unwrap(await clock.run(job!, at));
    assert(outcome.status === 'failed', `a throwing handler reported ${outcome.status}`);
    assert(outcome.note === 'D1 timed out', `the note is ${outcome.note}`);
    const again = unwrap(await clock.due('lg-1', since, at));
    assert(
      again.some((j) => j.id === job!.id),
      'a failed job was treated as done',
    );
  });

  await check(15, 'a claim past its lease is reclaimed rather than blocking forever', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    let hang = true;
    const handlers = new Map<JobKindId, JobHandler>([
      [
        'lineup.lock',
        {
          kind: 'lineup.lock',
          run: async () => (hang ? { status: 'running' } : { status: 'ok', note: 'done' }),
        },
      ],
    ]);
    const clock = await make(worldFor([league], threeWeeks(), { handlers }));
    unwrap(await clock.materialize('lg-1', NOW));
    const at = new Date('2026-09-11T00:20:00.000Z');
    const since = new Date('2026-09-01T00:00:00.000Z');
    const job = unwrap(await clock.due('lg-1', since, at)).find((j) => j.kind === 'lineup.lock')!;
    unwrap(await clock.run(job, at));
    // Still claimed a minute later, so a second sweep must not take it.
    const soon = await clock.run(job, new Date(at.getTime() + 60_000));
    assert(!soon.ok, 'a live claim was stolen');
    // An hour later the lease is long gone.
    hang = false;
    const later = new Date(at.getTime() + 60 * 60 * 1000);
    unwrap(await clock.sweep(later));
    const after = await clock.run(job, later);
    assert(after.ok, 'the job was still blocked an hour past its lease');
  });

  await check(16, 'a job with nothing to do is idle, not ok', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    // No slots at all, so the lock has nothing to stamp.
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const at = new Date('2026-09-11T00:20:00.000Z');
    const job = unwrap(await clock.due('lg-1', new Date('2026-09-01T00:00:00.000Z'), at)).find(
      (j) => j.kind === 'lineup.lock',
    )!;
    const outcome = unwrap(await clock.run(job, at));
    assert(outcome.status === 'idle', `a lock with no slots reported ${outcome.status}`);
  });

  // ── Reality moving underneath a league ──────────────────────────────────

  await check(17, 'a played period never moves; a future one does', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const world = worldFor([league], threeWeeks());
    const clock = await make(world);
    unwrap(await clock.materialize('lg-1', NOW));
    const before = unwrap(await clock.forLeague('lg-1'));
    const wasWeek1 = JSON.stringify(before.find((p) => p.index === 1));
    const wasWeek2 = JSON.stringify(before.find((p) => p.index === 2));

    // A fortnight on, weeks 1 and 2 have been played. Now the source moves
    // every game a day later — a thing no real schedule does, chosen because it
    // would move every boundary if anything were allowed to move.
    const later = new Date('2026-09-24T00:00:00.000Z');
    const shifted = threeWeeks().map((g) => ({ ...g, start: iso(Date.parse(g.start) + DAY) }));
    world.games.splice(0, world.games.length, ...shifted);
    await world.onGamesChanged?.();

    const change = unwrap(await clock.materialize('lg-1', later));
    assert(change.frozen === 2, `expected 2 frozen periods, got ${change.frozen}`);
    const after = unwrap(await clock.forLeague('lg-1'));
    assert(JSON.stringify(after.find((p) => p.index === 1)) === wasWeek1, 'a played period moved');
    assert(JSON.stringify(after.find((p) => p.index === 2)) === wasWeek2, 'a played period moved');
    const week3 = after.find((p) => p.index === 3)!;
    assert(
      week3.windows[0]!.firstKickoff === iso(Date.parse('2026-09-25T00:15:00.000Z') + DAY),
      `the future period did not follow the schedule: ${week3.windows[0]!.firstKickoff}`,
    );
  });

  await check(18, 'a kickoff that moves moves the lock and is reported', async () => {
    const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
    const world = worldFor([league], threeWeeks());
    const clock = await make(world);
    unwrap(await clock.materialize('lg-1', NOW));
    const inside = new Date('2026-09-12T00:00:00.000Z');
    const was = unwrap(await clock.stateOf('lg-1', inside)).current!.windows[0]!.lockAt;
    assert(was === '2026-09-11T00:15:00.000Z', `the first lock is ${was}`);

    // The league office moves the Thursday game out of the week. Same period,
    // later first kickoff, so the lock moves with it and the league is told:
    // accuracy first. B-93.
    const kept = world.games.filter((g) => g.id !== 'g-w1-0');
    world.games.splice(0, world.games.length, ...kept);
    await world.onGamesChanged?.();

    const change = unwrap(await clock.materialize('lg-1', NOW));
    assert(change.moves.length >= 1, 'the schedule moved and nothing was reported');
    const move = change.moves.find((m) => m.period === 1);
    assert(move, 'the moved period was not among the reported moves');
    assert(move!.from === was, `the move says it was ${move!.from}`);
    const now = unwrap(await clock.stateOf('lg-1', inside)).current!.windows[0]!.lockAt;
    assert(now === move!.to, 'the reported move and the new lock disagree');
    assert(now !== was, 'the schedule moved and the lock did not');

    // And re-reading an unchanged schedule reports nothing, so a league is not
    // told the same thing every time the projection runs.
    const quiet = unwrap(await clock.materialize('lg-1', NOW));
    assert(quiet.moves.length === 0, 'an unchanged schedule reported a move');
  });

  await check(19, 'nothing here reads the ambient clock', async () => {
    const league = leagueFor('lg-1', settingsFor({}));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    // A moment years from the real one. If anything inside consulted the
    // machine's clock, `current` would be the wrong period or none at all.
    const view = unwrap(await clock.stateOf('lg-1', new Date('2026-09-14T00:00:00.000Z')));
    assert(view.now === '2026-09-14T00:00:00.000Z', `the view says it is ${view.now}`);
    assert(view.current?.index === 1, `the current period is ${view.current?.index}`);
    const far = unwrap(await clock.stateOf('lg-1', new Date('2031-01-01T00:00:00.000Z')));
    assert(far.current === null, 'a league five years on still has a current period');
    assert(far.next === null, 'a league five years on still has a next period');
  });

  await check(
    20,
    'a weekly rule fires once a week, including the week the clock changes',
    async () => {
      const league = leagueFor(
        'lg-1',
        settingsFor({ waivers: { schedule: 'weekly', time: '01:30', weekday: 0 }, timeZone: CHI }),
      );
      const clock = await make(worldFor([league], threeWeeks()));
      unwrap(await clock.materialize('lg-1', NOW));
      // 01:30 on a Sunday, over the fall-back weekend of 2026-11-01 when the
      // local clock reads 01:30 twice, and the spring-forward weekend of
      // 2026-03-08 when 02:30 does not exist at all.
      for (const [from, to, label] of [
        ['2026-10-25T12:00:00.000Z', '2026-11-08T12:00:00.000Z', 'fall back'],
        ['2026-03-01T12:00:00.000Z', '2026-03-15T12:00:00.000Z', 'spring forward'],
      ] as const) {
        const view = unwrap(await clock.stateOf('lg-1', new Date(from)));
        assert(view.nextWaiverRunAt !== null, `${label}: no next waiver run`);
        const runs: string[] = [];
        let cursor = new Date(from);
        for (let i = 0; i < 6; i++) {
          const at = unwrap(await clock.stateOf('lg-1', cursor)).nextWaiverRunAt;
          if (at === null || Date.parse(at) > Date.parse(to)) break;
          runs.push(at);
          cursor = new Date(Date.parse(at) + 1000);
        }
        assert(runs.length === 2, `${label}: ${runs.length} runs in a fortnight, expected 2`);
        for (const at of runs) {
          assert(
            localTime(CHI, at) === '01:30',
            `${label}: a run lands at ${localTime(CHI, at)} local`,
          );
        }
      }
    },
  );

  await check(21, 'a trade deadline is the end of its period, to the millisecond', async () => {
    const league = leagueFor('lg-1', settingsFor({ deadlinePeriod: 2 }));
    const clock = await make(worldFor([league], threeWeeks()));
    unwrap(await clock.materialize('lg-1', NOW));
    const periods = unwrap(await clock.forLeague('lg-1'));
    const week2 = periods.find((p) => p.index === 2)!;
    const view = unwrap(await clock.stateOf('lg-1', NOW));
    assert(
      view.tradeDeadlineAt === week2.end,
      `the deadline is ${view.tradeDeadlineAt}, period 2 ends ${week2.end}`,
    );
    const none = leagueFor('lg-2', settingsFor({ deadlinePeriod: null }));
    const other = await make(worldFor([none], threeWeeks()));
    unwrap(await other.materialize('lg-2', NOW));
    const second = unwrap(await other.stateOf('lg-2', NOW));
    assert(second.tradeDeadlineAt === null, 'a league with no deadline has one');
  });

  await check(22, 'a league arriving mid-season finds its past already frozen', async () => {
    const league = leagueFor('lg-late', settingsFor({}));
    const clock = await make(worldFor([league], threeWeeks()));
    // Materialized for the first time in week 3.
    const at = new Date('2026-09-26T00:00:00.000Z');
    const change = unwrap(await clock.materialize('lg-late', at));
    assert(change.frozen === 0, 'a first materialize froze something it had never stored');
    const view = unwrap(await clock.stateOf('lg-late', at));
    assert(view.current?.index === 3, `the current period is ${view.current?.index}`);
    // Weeks 1 and 2 are in the past, so a second materialize must leave them.
    const second = unwrap(await clock.materialize('lg-late', at));
    assert(second.frozen === 2, `expected 2 frozen periods, got ${second.frozen}`);
    assert(second.moves.length === 0, 'nothing moved and a move was reported');
    // And `behindBy` is null until a sweep has actually run.
    assert(view.behindBy === null, `behindBy is ${view.behindBy} before any sweep`);
  });

  // ── 23 ────────────────────────────────────────────────────────────────
  //
  // THE CHECK THAT WAS MISSING, AND IT WAS MISSING FOR A YEAR OF DESIGN.
  // `per-player-game` is football's default lock rule and `planJobs` has always
  // put one lock job at each kickoff of a window, with the comment "each one
  // locks its own slots". The storage locked the whole window at the first of
  // them. Every later lock job of the week reported `idle` because nothing was
  // left to stamp, which is precisely what a correct run looks like on a second
  // pass - so the symptom was in the season goldens from the day they were
  // blessed and nothing was asserting against it.
  //
  // What it cost: a manager whose Monday-night starter was locked on the
  // Thursday could still be REFUSED nothing by `saveLineup` - the refusal is
  // per player and correctly allowed the swap - and the write, guarded by
  // `locked_at IS NULL`, then matched no rows and returned `ok` with the old
  // lineup. A silent fallback in the one place the house rules forbid one.
  await check(
    23,
    'a kickoff locks its own players and leaves the rest of the week alone',
    async () => {
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'per-player-game' }), 'in-season');
      // One slot per club playing in week one: HAW/OWL kick off Thursday, CAT/DOG
      // on the Monday. `threeWeeks()` gives each game its own pair. `GNU` plays
      // nobody — a pro bye, which has no kickoff of its own and must therefore
      // lock when the week ends rather than never.
      const clubs = ['HAW', 'OWL', 'FOX', 'ELK', 'RAM', 'BAT', 'CAT', 'DOG', 'GNU'];
      const slots = clubs.map((club, i) => ({
        leagueId: 'lg-1',
        teamId: 't-1',
        windowId: '2026-w01',
        slot: `START-${i}`,
        lockedAt: null,
        club,
      }));
      const clock = await make(worldFor([league], threeWeeks(), { slots }));
      unwrap(await clock.materialize('lg-1', NOW));

      // Run every lock job of week one, in order, each AT its own due time.
      const thursday = Date.parse('2026-09-11T00:15:00.000Z');
      const due = unwrap(
        await clock.due(
          'lg-1',
          new Date(Date.parse('2026-09-01T00:00:00.000Z')),
          // PAST THE WINDOW'S END (12:15 on the 15th), or the window-end lock is
          // not due yet and the last job in hand is the Monday kickoff.
          new Date('2026-09-15T13:00:00.000Z'),
        ),
      );
      const locks = due
        .filter((j) => j.kind === 'lineup.lock' && j.windowId === '2026-w01')
        .sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt));
      assert(
        locks.length === 5,
        `week one planned ${locks.length} lock jobs, expected four kickoffs and the window's end`,
      );

      // THE FIRST ONE STAMPS TWO SLOTS, NOT NINE.
      const first = unwrap(await clock.run(locks[0]!, new Date(thursday)));
      assert(
        first.status === 'ok' && (first.note ?? '').startsWith('2 slot'),
        `the Thursday kickoff reported "${first.note}" - it should lock HAW and OWL and nobody else`,
      );

      // AND EVERY LATER INSTANT STILL HAS WORK. Under the broken implementation
      // each of these was `idle`, which is why nothing ever noticed. The last of
      // them is the window's end, and the one slot it finds is the bye.
      const later = locks.slice(1);
      const last = later[later.length - 1]!;
      for (const job of later) {
        const outcome = unwrap(await clock.run(job, new Date(Date.parse(job.dueAt))));
        assert(
          outcome.status === 'ok',
          `the lock at ${job.dueAt} reported ${outcome.status}: its players were locked by an earlier instant`,
        );
        if (job === last) {
          assert(
            (outcome.note ?? '').startsWith('1 slot'),
            `the window's end reported "${outcome.note}" - it should find only the bye`,
          );
        }
      }

      // A SECOND PASS OVER THE WHOLE WEEK CHANGES NOTHING, so narrowing the lock
      // has not cost the idempotence check 12 asserts.
      for (const job of locks) {
        const again = unwrap(await clock.run(job, new Date(Date.parse(job.dueAt))));
        assert(
          again.status === 'idle',
          `re-running the lock at ${job.dueAt} stamped something again`,
        );
      }
    },
  );

  await check(
    24,
    'a draft time is one job with no period and no window, and it comes due once',
    async () => {
      // THE ONLY KIND WHOSE DUE INSTANT COMES FROM SETTINGS. Every other job
      // this package plans is derived from the calendar — a period opening, a
      // window locking, a waiver run on a Wednesday — and is therefore covered
      // by the checks that walk a season. This one is a single moment a
      // commissioner typed into a form, and the failure it guards against is
      // the expensive one: a league drafted twice, which cannot be undone
      // because the second board is not the first one.
      const draftAt = '2026-09-09T01:00:00.000Z';
      const league = leagueFor('lg-1', settingsFor({ draftScheduledAt: draftAt }), 'setup');
      // A league that never set a time. It must never get one of these.
      const quiet = leagueFor('lg-2', settingsFor({}), 'setup');

      // A RECORDING HANDLER, not the real runner: what belongs to this package
      // is when the job is offered and whether the ledger stops it being
      // offered twice. What the draft DOES needs players, plans and a board,
      // none of which is the clock's business — that half is `league:verify`.
      const drafted: string[] = [];
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'draft.start',
          {
            kind: 'draft.start',
            run: async ({ job }) => {
              if (drafted.includes(job.id)) return { status: 'idle', note: null };
              drafted.push(job.id);
              return { status: 'ok', note: `drafted at ${job.dueAt}` };
            },
          },
        ],
      ]);

      const clock = await make(worldFor([league, quiet], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      unwrap(await clock.materialize('lg-2', NOW));
      const since = new Date('2026-09-01T00:00:00.000Z');

      // BEFORE ANYTHING RUNS: a draft whose moment has not arrived is not work.
      const early = unwrap(
        await clock.due('lg-1', since, new Date(Date.parse(draftAt) - 1)),
      ).filter((j) => j.kind === 'draft.start');
      assert(early.length === 0, `a draft due at ${draftAt} was offered before it`);

      const at = new Date(Date.parse(draftAt) + 60 * 60 * 1000);
      const due = unwrap(await clock.due('lg-1', since, at)).filter(
        (j) => j.kind === 'draft.start',
      );
      assert(due.length === 1, `${due.length} draft jobs for one draft`);
      const job = due[0]!;
      assert(job.dueAt === draftAt, `the job is due at ${job.dueAt}, the draft at ${draftAt}`);
      // NO PERIOD AND NO WINDOW, and it matters: a handler that read either
      // would be reading the calendar for a job the calendar did not produce.
      assert(
        job.period === null && job.windowId === null,
        `the draft job names period ${String(job.period)} and window ${String(job.windowId)}`,
      );

      const first = unwrap(await clock.run(job, at));
      assert(first.status === 'ok', `the draft reported ${first.status}`);

      // AND IT IS NOT OFFERED AGAIN. The ledger has it; a sweep an hour later
      // over the same range finds nothing, which is what stops a league being
      // drafted a second time by a cron that is simply doing its job.
      const twice = unwrap(await clock.due('lg-1', since, at)).filter(
        (j) => j.kind === 'draft.start',
      );
      assert(twice.length === 0, 'the draft came due a second time');
      assert(drafted.length === 1, `the draft ran ${drafted.length} times`);

      const none = unwrap(await clock.due('lg-2', since, at)).filter(
        (j) => j.kind === 'draft.start',
      );
      assert(none.length === 0, 'a league with no draft time was given a draft job');
    },
  );

  await check(
    25,
    'a draft is reminded about before it happens, three times, and never after',
    async () => {
      // THE REMINDERS ARE LEADS ON THE SAME INSTANT, which is the only thing
      // about them this package decides — who they go to and what they say is
      // the league store's. What is checked here is that they are planned
      // BEFORE the draft rather than beside it, because a reminder that lands
      // after the thing it reminds you about is worse than none: it is the
      // product telling somebody their team was drafted while they slept and
      // calling it a reminder.
      const draftAt = '2026-09-09T12:00:00.000Z';
      const league = leagueFor('lg-1', settingsFor({ draftScheduledAt: draftAt }), 'setup');
      const seen: { kind: JobKindId; dueAt: string }[] = [];
      const record = (kind: JobKindId): JobHandler => ({
        kind,
        run: async ({ job }) => {
          seen.push({ kind, dueAt: job.dueAt });
          return { status: 'ok', note: null };
        },
      });
      const handlers = new Map<JobKindId, JobHandler>([
        ['notify.draft.plan', record('notify.draft.plan')],
        ['notify.draft.remind', record('notify.draft.remind')],
      ]);

      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      const since = new Date('2026-09-01T00:00:00.000Z');

      const due = unwrap(await clock.due('lg-1', since, new Date(Date.parse(draftAt))));
      const reminders = due.filter((j) => j.kind.startsWith('notify.draft.'));
      assert(reminders.length === 3, `${reminders.length} reminders for one draft`);
      for (const job of reminders) {
        assert(
          Date.parse(job.dueAt) < Date.parse(draftAt),
          `a ${job.kind} is due at ${job.dueAt}, which is not before ${draftAt}`,
        );
        assert(job.period === null && job.windowId === null, `${job.kind} names a period`);
      }
      // THREE DISTINCT MOMENTS, not one moment planned three times. The job id
      // is derived from the instant, so two reminders at the same instant
      // would be one row in the ledger and one of the three would never fire.
      const moments = new Set(reminders.map((j) => j.dueAt));
      assert(moments.size === 3, `${moments.size} distinct moments for three reminders`);

      const plan = reminders.filter((j) => j.kind === 'notify.draft.plan');
      assert(plan.length === 1, `${plan.length} plan reminders`);
      assert(
        Date.parse(plan[0]!.dueAt) <
          Math.min(...reminders.filter((j) => j !== plan[0]).map((j) => Date.parse(j.dueAt))),
        'the "you have ranked nobody" reminder is not the earliest one',
      );

      // AND NOTHING AFTER THE DRAFT. A sweep that wakes a week late finds the
      // draft and no reminders, because their moment is behind the draft's.
      const late = unwrap(
        await clock.due(
          'lg-1',
          new Date(Date.parse(draftAt)),
          new Date('2026-09-20T00:00:00.000Z'),
        ),
      );
      assert(
        !late.some((j) => j.kind.startsWith('notify.draft.')),
        'a reminder was planned after the draft it reminds about',
      );
    },
  );

  await check(
    26,
    'a job whose kind this deployment has no handler for is failed, not settled, and can be tried again',
    async () => {
      // THE WINDOW BETWEEN A PAGES BUILD AND A WORKER DEPLOY. The planner in
      // the newer code plans `draft.start`; the worker still running the
      // older code meets the row and has no handler for it. The first
      // version recorded `refused`, which is settled — so a draft that fell
      // due in that window was lost for good, one red row on the day and
      // green ever after (AUDIT-2026-09-23 P4). `failed` stays due.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      const handlers = new Map<JobKindId, JobHandler>([
        ['period.open', { kind: 'period.open', run: async () => ({ status: 'idle' }) }],
      ]);
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      const at = new Date('2026-09-11T00:20:00.000Z');
      const orphan = {
        id: 'lg-1|draft.start|2026-09-10T00:00:00.000Z',
        leagueId: 'lg-1',
        kind: 'draft.start' as const,
        dueAt: '2026-09-10T00:00:00.000Z',
        period: null,
        windowId: null,
      };
      const outcome = unwrap(await clock.run(orphan, at));
      assert(outcome.status === 'failed', `a handler-less job reported ${outcome.status}`);
      assert(
        (outcome.note ?? '').includes('draft.start'),
        `the note does not name the kind: ${outcome.note}`,
      );
      // Not settled: the next sweep can claim it again. A `refused` row
      // would still be claimable by this call — the store's `claim` only
      // refuses a live lease — so the proof is the attempt count AND the
      // status, and the break for this check turns the status alone.
      const again = unwrap(await clock.run(orphan, new Date(at.getTime() + 60_000)));
      assert(again.attempt === 2, `the second attempt is ${again.attempt}`);
      assert(again.status === 'failed', `the second attempt reported ${again.status}`);
    },
  );

  await check(
    27,
    'a job that failed is offered again by the next sweep, and not once it has succeeded',
    async () => {
      // THE PLANNER ONLY SEES `(lastSweepAt, now]`, so a job that failed in one
      // sweep is behind the next one's range. Check 14 proved a failed job is
      // not SETTLED by asking `due` over the same range twice — which a sweep
      // never does — and four places said "failed stays due" while the real
      // sweep never looked at it again (found 2026-09-24). This asks the sweep.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      let calls = 0;
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'period.open',
          {
            kind: 'period.open',
            run: async () => {
              calls++;
              if (calls === 1) throw new Error('D1 timed out');
              return { status: 'ok' };
            },
          },
        ],
      ]);
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      // Week 1 opens three days before its first kickoff: 2026-09-08T00:15Z.
      const first = new Date('2026-09-08T00:20:00.000Z');
      const one = unwrap(await clock.sweep(first)).outcomes;
      const failed = one.find((o) => o.kind === 'period.open');
      assert(failed?.status === 'failed', `the first attempt reported ${failed?.status}`);
      const two = unwrap(await clock.sweep(new Date(first.getTime() + 60_000))).outcomes;
      const retried = two.find((o) => o.jobId === failed!.jobId);
      assert(retried, 'the next sweep did not offer the failed job again');
      assert(retried!.attempt === 2, `the retry is attempt ${retried!.attempt}`);
      assert(retried!.status === 'ok', `the retry reported ${retried!.status}`);
      const three = unwrap(await clock.sweep(new Date(first.getTime() + 120_000))).outcomes;
      assert(
        !three.some((o) => o.jobId === failed!.jobId),
        'a job that succeeded on its retry was run a third time',
      );
    },
  );

  await check(
    28,
    'a league somebody made never reads a simulated game; a seeded league reads both',
    async () => {
      // THE LOCAL SEEDER'S WORLD SHARES `games` WITH THE REAL SCHEDULE, and on
      // 2026-09-24 it held a simulated week-3 slate a day before the real
      // Thursday game. A league somebody made must open, lock and score at
      // real kickoffs; a seeded league's players only play simulated games.
      const games = [
        ...threeWeeks(),
        gameAt('g-sim-1', '2026-09-10T00:15:00.000Z', 1, 'SIM', 'BOT'),
      ];
      const real = leagueFor('lg-real', settingsFor({ lockRule: 'first-game' }));
      const seeded = leagueFor(
        'lg-seed',
        settingsFor({ lockRule: 'first-game' }),
        'in-season',
        'world-test',
      );
      const clock = await make(worldFor([real, seeded], games, { simGames: ['g-sim-1'] }));
      unwrap(await clock.materialize('lg-real', NOW));
      unwrap(await clock.materialize('lg-seed', NOW));
      const kickoff = async (id: string): Promise<string | null> => {
        const periods = unwrap(await clock.forLeague(id));
        return periods.find((p) => p.index === 1)?.windows[0]?.firstKickoff ?? null;
      };
      const mine = await kickoff('lg-real');
      assert(
        mine === '2026-09-11T00:15:00.000Z',
        `the league somebody made opens week 1 at ${mine}, a simulated kickoff`,
      );
      const theirs = await kickoff('lg-seed');
      assert(
        theirs === '2026-09-10T00:15:00.000Z',
        `the seeded league lost its simulated game: week 1 opens at ${theirs}`,
      );
    },
  );

  await check(
    29,
    'the season opens straight after the draft, and is looked at again as each regular period starts',
    async () => {
      // PLAN-2026-09-24 item C. Planned at the draft instant, ORDERED AFTER
      // `draft.start` (a schedule drawn before the draft has no rosters to
      // draw), and at the start of every regular period — never an `off` one,
      // which is a week before the league's own season begins.
      const draftAt = '2026-09-09T02:00:00.000Z';
      const league = leagueFor(
        'lg-1',
        settingsFor({ lockRule: 'first-game', draftScheduledAt: draftAt, firstPeriod: 2 }),
        'setup',
      );
      const record = (kind: JobKindId): [JobKindId, JobHandler] => [
        kind,
        { kind, run: async () => ({ status: 'idle' }) },
      ];
      const handlers = new Map<JobKindId, JobHandler>([
        record('draft.start'),
        record('season.open'),
        record('period.open'),
      ]);
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      const periods = unwrap(await clock.forLeague('lg-1'));
      const due = unwrap(
        await clock.due(
          'lg-1',
          new Date('2026-09-01T00:00:00.000Z'),
          new Date('2026-10-01T00:00:00.000Z'),
        ),
      );
      const atDraft = due.filter((j) => j.dueAt === draftAt).map((j) => j.kind);
      assert(
        atDraft.join(',') === 'draft.start,season.open',
        `at the draft instant the order is [${atDraft.join(', ')}]`,
      );
      const opens = due.filter((j) => j.kind === 'season.open' && j.dueAt !== draftAt);
      const starts = periods.filter((p) => p.index >= 2).map((p) => p.start);
      assert(
        opens.map((j) => j.dueAt).join(',') === starts.join(','),
        `season.open at [${opens.map((j) => j.dueAt).join(', ')}], ` +
          `expected each regular period's start [${starts.join(', ')}]`,
      );
      const week1 = periods.find((p) => p.index === 1)!;
      assert(
        !due.some((j) => j.kind === 'season.open' && j.dueAt === week1.start),
        'season.open was planned at the start of an off week',
      );
    },
  );

  await check(
    30,
    'a period opening carries each team’s last lineup into its window, once',
    async () => {
      // BACKLOG E16 and PLAN-2026-09-24 item F. Team 1 set week 1 and nothing
      // since, and has traded one of its three starters away: the two it
      // still holds are carried into week 3, across the empty week 2 — "the
      // previous period by number" carries nothing here, which is the break.
      // Team 2 set week 2, and had already put one player into week 3 itself:
      // that slot is kept, that player is not placed twice, and only the
      // slot nobody filled is carried. Team 1's week 3 has an empty cell in
      // it, as the seeder writes a week's empty cells: that is filled, not kept.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }), 'in-season');
      const slot = (
        teamId: string,
        windowId: string,
        slotIndex: number,
        playerId: string | null,
        club: string,
      ) => ({
        leagueId: 'lg-1',
        teamId,
        windowId,
        slot: 'START',
        slotIndex,
        playerId,
        club,
        lockedAt: null,
      });
      const world = worldFor([league], threeWeeks(), {
        slots: [
          slot('t-1', '2026-w01', 0, 'p-a', 'HAW'),
          slot('t-1', '2026-w01', 1, 'p-b', 'OWL'),
          slot('t-1', '2026-w01', 2, 'p-c', 'FOX'),
          slot('t-1', '2026-w03', 0, null, 'HAW'),
          slot('t-2', '2026-w02', 0, 'p-e', 'RAM'),
          slot('t-2', '2026-w02', 1, 'p-d', 'ELK'),
          slot('t-2', '2026-w02', 2, 'p-f', 'BAT'),
          slot('t-2', '2026-w03', 0, 'p-d', 'ELK'),
        ],
        rosters: [
          { teamId: 't-1', playerId: 'p-a' },
          { teamId: 't-1', playerId: 'p-b' },
          { teamId: 't-2', playerId: 'p-d' },
          { teamId: 't-2', playerId: 'p-e' },
          { teamId: 't-2', playerId: 'p-f' },
        ],
      });
      const clock = await make(world);
      const readSlots = world.readSlots;
      assert(readSlots, 'the implementation gave the suite no way to read lineup rows');
      unwrap(await clock.materialize('lg-1', NOW));
      const due = unwrap(
        await clock.due(
          'lg-1',
          new Date('2026-09-01T00:00:00.000Z'),
          new Date('2026-10-01T00:00:00.000Z'),
        ),
      );
      const open = due.find((j) => j.kind === 'period.open' && j.period === 3);
      assert(open, 'no period.open was planned for week 3');
      const at = new Date(open.dueAt);
      const first = unwrap(await clock.run(open, at));
      assert(
        first.status === 'ok' && (first.note ?? '').includes('2 lineup(s) carried'),
        `the opening reported ${first.status} "${first.note ?? ''}", expected two lineups carried`,
      );
      const read = async (teamId: string): Promise<string> =>
        (await readSlots(teamId, '2026-w03'))
          .map(
            (r) =>
              `${r.slotIndex}:${r.playerId ?? '-'}:${r.setBy ?? '-'}:${r.lockedAt === null ? 'open' : 'locked'}`,
          )
          .sort()
          .join(' ');
      const one = await read('t-1');
      assert(
        one === '0:p-a:carried:open 1:p-b:carried:open',
        `team 1's week 3 is [${one}], expected the two from week 1 it still holds`,
      );
      const two = await read('t-2');
      assert(
        two === '0:p-d:usr-clock:open 2:p-f:carried:open',
        `team 2's week 3 is [${two}], expected its own slot kept and only the empty one carried`,
      );
      const again = unwrap(await clock.run(open, at));
      assert(
        again.status === 'idle',
        `a second opening reported ${again.status} "${again.note ?? ''}"`,
      );
    },
  );

  await check(
    31,
    'a week\u2019s lines landing plans a scoring pass at that instant, for that week',
    async () => {
      // BACKLOG L7. The league's season starts in week 2, so week 1 is `off`.
      // Four stats runs: one in week 2 that wrote lines — the only landing —
      // and three that are not: one in the off week, one in week 2 that wrote
      // nothing, and one for another sport.
      //
      // AND IT STAYS DUE UNTIL IT HAS RUN. A run's recorded instant is when it
      // started, and the sweep that fires in the same minute reads it while it
      // is still running; the next sweep's range starts after that instant. A
      // pass planned from the range alone — the first cut of this, 2026-09-24 —
      // would never run at all on the day it mattered.
      const league = leagueFor(
        'lg-1',
        settingsFor({ lockRule: 'first-game', firstPeriod: 2 }),
        'in-season',
      );
      // AND THE MATCHUP CUE'S REFRESH (Phase 1): the same landing asks for
      // one, with no week on it, when the deployment has a handler for it.
      const handlers = new Map<JobKindId, JobHandler>([
        ['matchup.score', { kind: 'matchup.score', run: async () => ({ status: 'idle' }) }],
        [
          'league.versus.refresh',
          { kind: 'league.versus.refresh', run: async () => ({ status: 'idle' }) },
        ],
      ]);
      const landed = '2026-09-20T09:00:00.000Z';
      const clock = await make(
        worldFor([league], threeWeeks(), {
          handlers,
          landings: [
            { sport: 'football', at: '2026-09-15T09:00:00.000Z', rows: 50 },
            { sport: 'football', at: landed, rows: 120 },
            { sport: 'football', at: '2026-09-21T09:00:00.000Z', rows: 0 },
            { sport: 'hockey', at: '2026-09-19T09:00:00.000Z', rows: 10 },
          ],
        }),
      );
      unwrap(await clock.materialize('lg-1', NOW));
      const periods = unwrap(await clock.forLeague('lg-1'));
      const ends = new Set(periods.flatMap((p) => p.windows.map((w) => w.end)));
      const due = unwrap(
        await clock.due(
          'lg-1',
          new Date('2026-09-01T00:00:00.000Z'),
          new Date('2026-09-21T10:00:00.000Z'),
        ),
      );
      const extra = due.filter((j) => j.kind === 'matchup.score' && !ends.has(j.dueAt));
      assert(
        extra.length === 1 &&
          extra[0]!.dueAt === landed &&
          extra[0]!.period === 2 &&
          extra[0]!.windowId === '2026-w02',
        `passes planned at landings: [${extra
          .map((j) => `${j.dueAt} p${j.period ?? '-'} ${j.windowId ?? '-'}`)
          .join(', ')}], expected one at ${landed} for week 2`,
      );
      const refreshes = due.filter((j) => j.kind === 'league.versus.refresh');
      assert(
        refreshes.length === 1 &&
          refreshes[0]!.dueAt === landed &&
          refreshes[0]!.period === null &&
          refreshes[0]!.windowId === null,
        `cue refreshes planned at landings: [${refreshes
          .map((j) => `${j.dueAt} p${j.period ?? '-'}`)
          .join(', ')}], expected one at ${landed} with no week`,
      );
      const later = new Date(Date.parse(landed) + 60 * 1000);
      const next = unwrap(await clock.due('lg-1', new Date(landed), later));
      const pass = next.find((j) => j.kind === 'matchup.score' && j.dueAt === landed);
      assert(pass, 'the sweep after the landing did not offer its pass, and nothing ever will');
      unwrap(await clock.run(pass, later));
      const after = unwrap(await clock.due('lg-1', later, new Date(later.getTime() + 60 * 1000)));
      assert(
        !after.some((j) => j.kind === 'matchup.score' && j.dueAt === landed),
        'a pass that has run was offered again',
      );
    },
  );

  await check(
    32,
    'a league with no waivers has no run: none is planned and none is next',
    async () => {
      // PLAN-2026-09-24 rule 9. A league whose waiver system is `none` is first
      // come, first served, and still carries a processing time like every
      // league does. Until W6 the planner planned a `waivers.process` from that
      // time every week and the clock named it as the next run, so the league
      // was told a run was coming that could never decide anything. The same
      // rule in a league WITH waivers is the control: it plans two in a
      // fortnight and names the next, so a planner that planned nothing for
      // anybody cannot pass.
      const handlers = new Map<JobKindId, JobHandler>([
        ['waivers.process', { kind: 'waivers.process', run: async () => ({ status: 'idle' }) }],
      ]);
      const rule = { schedule: 'weekly' as const, time: '03:00', weekday: 3 };
      const none = leagueFor('lg-none', settingsFor({ waivers: rule, waiverSystem: 'none' }));
      const faab = leagueFor('lg-faab', settingsFor({ waivers: rule }));
      const clock = await make(worldFor([none, faab], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-none', NOW));
      unwrap(await clock.materialize('lg-faab', NOW));
      const from = new Date('2026-09-10T00:00:00.000Z');
      const to = new Date('2026-09-24T00:00:00.000Z');
      const runs = async (id: string) =>
        unwrap(await clock.due(id, from, to)).filter((j) => j.kind === 'waivers.process');
      const planned = await runs('lg-none');
      const control = await runs('lg-faab');
      assert(
        planned.length === 0,
        `a league with no waivers had ${planned.length} run(s) planned: ${planned.map((j) => j.dueAt).join(', ')}`,
      );
      assert(
        control.length === 2,
        `the league with waivers had ${control.length} runs, expected 2`,
      );
      const view = unwrap(await clock.stateOf('lg-none', from));
      assert(
        view.nextWaiverRunAt === null,
        `a league with no waivers names a next run at ${view.nextWaiverRunAt}`,
      );
      const other = unwrap(await clock.stateOf('lg-faab', from));
      assert(other.nextWaiverRunAt !== null, 'the league with waivers names no next run');
    },
  );

  await check(
    34,
    "the lineup warning is planned at every lead before the week's first lock, and the last call ten minutes before it",
    async () => {
      // D3, NOTIFICATIONS.md §3.4: one `notify.lineup.warn` per lead a manager
      // can choose (12 h, 4 h, 2 h, 1 h, 30 min) and one `notify.lineup.last`,
      // all timed off the week's FIRST lock — Thursday's kickoff, not
      // Sunday's — and none at all for a league with no handler for them
      // (§4: a kind nothing can run is never planned).
      const warn: JobHandler = {
        kind: 'notify.lineup.warn',
        run: async () => ({ status: 'idle' }),
      };
      const last: JobHandler = {
        kind: 'notify.lineup.last',
        run: async () => ({ status: 'idle' }),
      };
      const handlers = new Map<JobKindId, JobHandler>([
        ['notify.lineup.warn', warn],
        ['notify.lineup.last', last],
      ]);
      const league = leagueFor('lg-warn', settingsFor({ lockRule: 'per-player-game' }));
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-warn', NOW));
      // Week 2: Thursday 2026-09-18T00:15Z is the first kickoff.
      const from = new Date('2026-09-17T00:00:00.000Z');
      const to = new Date('2026-09-18T00:15:00.000Z');
      const planned = unwrap(await clock.due('lg-warn', from, to));
      const at = (kind: JobKindId) =>
        planned
          .filter((j) => j.kind === kind)
          .map((j) => j.dueAt)
          .sort();
      const expected = [
        '2026-09-17T12:15:00.000Z',
        '2026-09-17T20:15:00.000Z',
        '2026-09-17T22:15:00.000Z',
        '2026-09-17T23:15:00.000Z',
        '2026-09-17T23:45:00.000Z',
      ];
      assert(
        JSON.stringify(at('notify.lineup.warn')) === JSON.stringify(expected),
        `warnings planned at ${at('notify.lineup.warn').join(', ') || 'nothing'}`,
      );
      assert(
        JSON.stringify(at('notify.lineup.last')) === JSON.stringify(['2026-09-18T00:05:00.000Z']),
        `last call planned at ${at('notify.lineup.last').join(', ') || 'nothing'}`,
      );
      // The same league with no handler for either, in a world of its own.
      const bare = await make(
        worldFor(
          [leagueFor('lg-quiet', settingsFor({ lockRule: 'per-player-game' }))],
          threeWeeks(),
        ),
      );
      unwrap(await bare.materialize('lg-quiet', NOW));
      const none = unwrap(await bare.due('lg-quiet', from, to)).filter((j) =>
        j.kind.startsWith('notify.lineup'),
      );
      assert(none.length === 0, `${none.length} warning(s) planned with no handler to run them`);
    },
  );

  // ── The clock plans ahead (DATA_LAYER_DESIGN Phase 1) ───────────────────
  //
  // A sweep reads the due rows a league's planning job wrote ahead, sends
  // each league's as one message, and with no bus runs them itself before it
  // returns. These checks ask the sweep as a cron would and read what ran;
  // the ones about the sending give the world a bus that keeps what it was
  // sent and consume it by hand.

  await check(
    35,
    "a finished league is still planned for its last week's correction, and for that correction's retry six days on",
    async () => {
      // `season.rollover` marks a league `complete` at the end of its last
      // period, and that week's `stats.correct` is due two days LATER. A
      // planner that dropped a league at `complete` would never correct its
      // last week; one that dropped it at the correction would never retry a
      // correction that failed. Week 3 is this league's last.
      const done = leagueFor('lg-done', settingsFor({ lockRule: 'first-game' }), 'complete');
      const calls = new Map<string, number>();
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'stats.correct',
          {
            kind: 'stats.correct',
            run: async ({ job }) => {
              const n = (calls.get(job.id) ?? 0) + 1;
              calls.set(job.id, n);
              if (n === 1) throw new Error('D1 timed out');
              return { status: 'ok' };
            },
          },
        ],
      ]);
      const clock = await make(worldFor([done], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-done', NOW));
      const periods = unwrap(await clock.forLeague('lg-done'));
      const lastEnd = Math.max(...periods.map((p) => Date.parse(p.end)));
      const correction = lastEnd + 2 * DAY;
      const first = unwrap(await clock.sweep(new Date(correction + 60_000))).outcomes;
      const failed = first.find(
        (o) => o.kind === 'stats.correct' && Date.parse(o.dueAt) === correction,
      );
      assert(
        failed?.status === 'failed',
        `the last week's correction, due two days after the league finished, reported ${failed?.status ?? 'nothing: the league was not planned'}`,
      );
      const later = unwrap(await clock.sweep(new Date(correction + 6 * DAY))).outcomes;
      const retried = later.find((o) => o.jobId === failed!.jobId);
      assert(
        retried?.status === 'ok' && retried.attempt === 2,
        `six days on, the failed correction was ${retried === undefined ? 'not sent again' : `attempt ${retried.attempt}, ${retried.status}`}`,
      );
    },
  );

  await check(
    36,
    'a finished league leaves the clock once its last correction and the week of retries after it are past',
    async () => {
      // THE OTHER HALF OF 35. Until Phase 0 every league ever made was swept
      // every minute, and planned a waiver run every week forever. Week 3 ends
      // 2026-09-29T12:15Z, so the finished league is planned to 2026-10-08T12:15Z
      // and no further. Its waiver run on Wednesday 2026-10-07 is still run
      // (the control that it was being planned); the one on 2026-10-14 is
      // not, while the league still playing gets both.
      const rule = { schedule: 'weekly' as const, time: '03:00', weekday: 3 };
      const settings = settingsFor({ lockRule: 'first-game', waivers: rule });
      const done = leagueFor('lg-done', settings, 'complete');
      const live = leagueFor('lg-live', settings, 'in-season');
      const ran: string[] = [];
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'waivers.process',
          {
            kind: 'waivers.process',
            run: async ({ job }) => {
              ran.push(`${job.leagueId} ${job.dueAt}`);
              return { status: 'idle' };
            },
          },
        ],
      ]);
      const clock = await make(worldFor([done, live], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-done', NOW));
      unwrap(await clock.materialize('lg-live', NOW));
      unwrap(await clock.sweep(new Date('2026-10-08T12:00:00.000Z')));
      unwrap(await clock.sweep(new Date('2026-10-14T08:01:00.000Z')));
      const want = [
        'lg-done 2026-10-07T08:00:00.000Z',
        'lg-live 2026-10-07T08:00:00.000Z',
        'lg-live 2026-10-14T08:00:00.000Z',
      ];
      assert(
        JSON.stringify([...ran].sort()) === JSON.stringify(want),
        `waiver runs [${ran.join(', ')}], expected [${want.join(', ')}]`,
      );
    },
  );

  await check(
    39,
    'a minute is swept once: a second sweep of the same instant, or of an earlier one, runs nothing',
    async () => {
      // The platform replays a scheduled event, and a late invocation can
      // arrive after a later minute has started. A lock that keeps failing is
      // sent again a minute on, so a duplicate that swept would run it again
      // in the same minute.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      let calls = 0;
      // Read through a function: an assert narrows a plain `calls` for good.
      const ran = (): number => calls;
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'lineup.lock',
          {
            kind: 'lineup.lock',
            run: async () => {
              calls++;
              throw new Error('D1 timed out');
            },
          },
        ],
      ]);
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      const at = new Date('2026-09-11T00:20:00.000Z');
      unwrap(await clock.sweep(at));
      assert(ran() === 1, `the first sweep ran the lock ${ran()} time(s)`);
      const again = await clock.sweep(at);
      const earlier = await clock.sweep(new Date(at.getTime() - 60_000));
      assert(
        !again.ok && !earlier.ok && ran() === 1,
        `the same instant ${again.ok ? 'swept' : 'was refused'}, an earlier one ${earlier.ok ? 'swept' : 'was refused'}, and the lock ran ${ran()} time(s)`,
      );
      unwrap(await clock.sweep(new Date(at.getTime() + 60_000)));
      assert(
        ran() === 2,
        `the next minute ran the failed lock ${ran() - 1} more time(s), not once`,
      );
    },
  );

  await check(
    41,
    'a finished league is kept in the clock while a job its rows decide is still to come, and leaves once it has run',
    async () => {
      // A league with no week left still takes a rule change — it counts from
      // next season — so a vote can open after the calendar has let the league
      // go (2026-10-08T12:15Z, as in 36). Its close, on 2026-10-20, must still
      // run: the kind reports the instant, the planner plans it whatever the
      // calendar says, and once it has run there is nothing to keep the
      // league for.
      const rule = { schedule: 'weekly' as const, time: '03:00', weekday: 3 };
      const done = leagueFor(
        'lg-done',
        settingsFor({ lockRule: 'first-game', waivers: rule }),
        'complete',
      );
      const closesAt = Date.parse('2026-10-20T12:00:00.000Z');
      let closed = false;
      const ran: string[] = [];
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'rules.vote.close',
          {
            kind: 'rules.vote.close',
            instants: async (leagueId) => (leagueId === 'lg-done' && !closed ? [closesAt] : []),
            run: async ({ job }) => {
              closed = true;
              ran.push(`vote ${job.dueAt}`);
              return { status: 'ok' };
            },
          },
        ],
        [
          'waivers.process',
          {
            kind: 'waivers.process',
            run: async ({ job }) => {
              ran.push(`waivers ${job.dueAt}`);
              return { status: 'idle' };
            },
          },
        ],
      ]);
      const clock = await make(worldFor([done], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-done', NOW));
      unwrap(await clock.sweep(new Date('2026-10-20T12:01:00.000Z')));
      assert(
        ran.includes('vote 2026-10-20T12:00:00.000Z'),
        `the off-season vote did not close: the sweep ran [${ran.join(', ')}]`,
      );
      const before = ran.length;
      unwrap(await clock.sweep(new Date('2026-10-21T08:01:00.000Z')));
      assert(
        ran.length === before,
        `once the vote had closed the league was still run: [${ran.slice(before).join(', ')}]`,
      );
    },
  );

  /** A consumer's budget: allows `n` asks, then refuses every one after. */
  const asks = (n: number): (() => boolean) => {
    let asked = 0;
    return () => ++asked <= n;
  };

  /** What a message names, less the clock's own planning job. */
  const namedJobs = (work: { jobIds: readonly string[] }): string[] =>
    work.jobIds.filter((id) => !id.includes('|clock.plan|')).sort();
  /** How many messages a bus has been sent. Through a function: an assert narrows a plain length for good. */
  const sentCount = (b: { sent: readonly unknown[] }): number => b.sent.length;

  await check(
    42,
    'the planning job writes what the planner names: a sweep sends exactly the jobs due by its instant, the next day the daily planning run, and a later sweep the rest',
    async () => {
      // Three sweeps with a bus that keeps what it was sent. The first sends
      // the league's planning rows and nothing else, because nothing else has
      // been planned; consumed, they plan 48 hours ahead. The second sends
      // exactly what `due` names as due by then. The third, a day on, sends
      // the league's daily planning run at its own instant and nothing else.
      // The fourth, a week on, sends what became due since.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      const record = (kind: JobKindId): [JobKindId, JobHandler] => [
        kind,
        { kind, run: async () => ({ status: 'ok' }) },
      ];
      const handlers = new Map<JobKindId, JobHandler>([
        record('lineup.lock'),
        record('period.open'),
      ]);
      const bus = recordingBus();
      const clock = await make(worldFor([league], threeWeeks(), { handlers, bus }));
      unwrap(await clock.materialize('lg-1', NOW));
      const at = new Date('2026-09-12T00:00:00.000Z');
      unwrap(await clock.sweep(at));
      assert(
        sentCount(bus) === 1 &&
          bus.sent[0]!.leagueId === 'lg-1' &&
          namedJobs(bus.sent[0]!).length === 0,
        `the first sweep sent ${bus.sent.length} message(s): [${bus.sent.map((m) => m.jobIds.join(' ')).join(' | ')}]`,
      );
      unwrap(await clock.consume(bus.sent[0]!, at));

      const second = new Date(at.getTime() + 60_000);
      unwrap(await clock.sweep(second));
      const expected = unwrap(await clock.due('lg-1', new Date(at.getTime() - 7 * DAY), second))
        .map((j) => j.id)
        .sort();
      assert(
        sentCount(bus) === 2 &&
          JSON.stringify(namedJobs(bus.sent[1]!)) === JSON.stringify(expected),
        `the second sweep sent [${bus.sent[1]?.jobIds.join(', ') ?? 'nothing'}], the planner names [${expected.join(', ')}]`,
      );
      unwrap(await clock.consume(bus.sent[1]!, second));

      // Two days on: the daily planning run at the league's own instant, and
      // the replan the jobs that did something asked for — nothing else.
      const third = new Date('2026-09-14T00:00:00.000Z');
      unwrap(await clock.sweep(third));
      const daily = jobId('lg-1', 'clock.plan', dailyPlanAt('lg-1', at).toISOString());
      const replan = jobId('lg-1', 'clock.plan', second.toISOString());
      const planning = [daily, replan].sort();
      assert(
        sentCount(bus) === 3 &&
          JSON.stringify([...bus.sent[2]!.jobIds].sort()) === JSON.stringify(planning),
        `two days on the sweep sent [${bus.sent[2]?.jobIds.join(', ') ?? 'nothing'}], expected the daily planning run and the replan the week's opening asked for: [${planning.join(', ')}]`,
      );
      unwrap(await clock.consume(bus.sent[2]!, third));

      // A week on: what the plan of two days ago reached, then — the daily
      // run of that message consumed — what it planned since. Between the two
      // messages, exactly what the planner names as due since the second sweep.
      const fourth = new Date('2026-09-19T00:00:00.000Z');
      const fifth = new Date(fourth.getTime() + 60_000);
      unwrap(await clock.sweep(fourth));
      assert(sentCount(bus) === 4, `a week on the sweep sent nothing`);
      // The planner's answer, asked before anything of it has run.
      const later = unwrap(await clock.due('lg-1', second, fifth))
        .map((j) => j.id)
        .sort();
      unwrap(await clock.consume(bus.sent[3]!, fourth));
      unwrap(await clock.sweep(fifth));
      const sentSince = bus.sent
        .slice(3)
        .flatMap((w) => namedJobs(w))
        .sort();
      assert(
        JSON.stringify(sentSince) === JSON.stringify(later),
        `a week on the sweeps sent [${sentSince.join(', ')}], the planner names [${later.join(', ')}]`,
      );
    },
  );

  await check(
    43,
    'a failed job backs off: sent again a minute on, five minutes after its second failure, fifteen after its third, then hourly, and given up on after seven days',
    async () => {
      // A failing job cost a row a minute for a week; now each failure waits
      // longer. The week-1 lock fails every time it runs; what is asserted is
      // the minutes it runs at, and that the week-2 lock, due a week later, is
      // its own row.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      const calls = new Map<string, number>();
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'lineup.lock',
          {
            kind: 'lineup.lock',
            run: async ({ job }) => {
              calls.set(job.id, (calls.get(job.id) ?? 0) + 1);
              throw new Error('D1 timed out');
            },
          },
        ],
      ]);
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      const lock = jobId('lg-1', 'lineup.lock', '2026-09-11T00:15:00.000Z');
      const minute = (m: number) => new Date(Date.parse('2026-09-11T00:20:00.000Z') + m * 60_000);
      const tries = (): number => calls.get(lock) ?? 0;
      const expect = async (at: Date, want: number, why: string) => {
        unwrap(await clock.sweep(at));
        assert(tries() === want, `${why}: the lock had run ${tries()} time(s), expected ${want}`);
      };
      await expect(minute(0), 1, 'at the first sweep');
      await expect(minute(1), 2, 'a minute after the first failure');
      await expect(minute(4), 2, 'three minutes after the second failure');
      await expect(minute(6), 3, 'five minutes after the second failure');
      await expect(minute(20), 3, 'fourteen minutes after the third');
      await expect(minute(21), 4, 'fifteen minutes after the third');
      await expect(minute(80), 4, 'fifty-nine minutes after the fourth');
      await expect(minute(81), 5, 'an hour after the fourth');
      await expect(new Date('2026-09-18T00:16:00.000Z'), 6, 'seven days on, its last try');
      await expect(new Date('2026-09-18T00:17:00.000Z'), 6, 'after it was given up on');
    },
  );

  await check(
    44,
    'a league has one message in flight at a time: while its rows are out with a consumer the sweep sends it nothing more, and a sweep past its limit sends the leagues it can and the rest next minute',
    async () => {
      // Three leagues drafting at one instant and a limit of one row a sweep.
      // Each sweep sends one league's planning row; a league whose row is out
      // is not sent again, so the fourth sweep sends nothing — and once the
      // first league's message is consumed, its two draft-day jobs go out
      // one message at a time, never two messages at once.
      const draftAt = '2026-09-09T02:00:00.000Z';
      const leagues = ['lg-a', 'lg-b', 'lg-c'].map((id) =>
        leagueFor(id, settingsFor({ draftScheduledAt: draftAt }), 'setup'),
      );
      const record = (kind: JobKindId): [JobKindId, JobHandler] => [
        kind,
        { kind, run: async () => ({ status: 'ok' }) },
      ];
      const handlers = new Map<JobKindId, JobHandler>([
        record('draft.start'),
        record('season.open'),
      ]);
      const bus = recordingBus();
      const clock = await make(worldFor(leagues, threeWeeks(), { handlers, bus }));
      const minute = (m: number) => new Date(Date.parse(draftAt) + m * 60_000);
      for (const m of [1, 2, 3, 4]) unwrap(await clock.sweep(minute(m), { limit: 1 }));
      const sentTo = bus.sent.map((w) => w.leagueId);
      assert(
        JSON.stringify(sentTo) === JSON.stringify(['lg-a', 'lg-b', 'lg-c']),
        `four sweeps of one row sent to [${sentTo.join(', ')}], expected each league once and the fourth nothing`,
      );
      unwrap(await clock.consume(bus.sent[0]!, minute(4)));
      unwrap(await clock.sweep(minute(5), { limit: 1 }));
      unwrap(await clock.sweep(minute(6), { limit: 1 }));
      const after = bus.sent.slice(3);
      assert(
        after.length === 1 && after[0]!.leagueId === 'lg-a' && after[0]!.jobIds.length === 1,
        `with league A's draft-day jobs ready and one out, two sweeps sent [${after.map((w) => `${w.leagueId}: ${w.jobIds.join(' ')}`).join(' | ')}], expected one row for A once`,
      );
      unwrap(await clock.consume(after[0]!, minute(6)));
      unwrap(await clock.sweep(minute(7), { limit: 1 }));
      const next = bus.sent.slice(4);
      assert(
        next.length === 1 && next[0]!.leagueId === 'lg-a',
        `with the first job consumed the next sweep sent [${next.map((w) => w.leagueId).join(', ')}], expected league A's other row`,
      );
    },
  );

  await check(
    45,
    "a consumer stopped by its budget hands the league's remaining rows back to the next sweep, and a message delivered twice runs nothing twice",
    async () => {
      // A draft and the season opening at one instant. The consumer may run
      // one job: the draft runs, the opening goes back, and the next sweep
      // sends it alone. The first message delivered again names a row that is
      // gone and one that is still due, and runs only the second — once.
      const draftAt = '2026-09-09T02:00:00.000Z';
      const league = leagueFor('lg-1', settingsFor({ draftScheduledAt: draftAt }), 'setup');
      const ran: string[] = [];
      const record = (kind: JobKindId): [JobKindId, JobHandler] => [
        kind,
        {
          kind,
          run: async () => {
            ran.push(kind);
            return { status: 'ok' };
          },
        },
      ];
      const handlers = new Map<JobKindId, JobHandler>([
        record('draft.start'),
        record('season.open'),
      ]);
      const bus = recordingBus();
      const clock = await make(worldFor([league], threeWeeks(), { handlers, bus }));
      const minute = (m: number) => new Date(Date.parse(draftAt) + m * 60_000);
      unwrap(await clock.sweep(minute(1)));
      unwrap(await clock.consume(bus.sent[0]!, minute(1)));
      unwrap(await clock.sweep(minute(2)));
      const work = bus.sent[1];
      assert(
        work !== undefined && namedJobs(work).length === 2,
        `the sweep after planning sent [${work?.jobIds.join(', ') ?? 'nothing'}], expected the draft and the opening`,
      );
      unwrap(await clock.consume(work, minute(2), { mayContinue: asks(1) }));
      assert(
        JSON.stringify(ran) === JSON.stringify(['draft.start']),
        `a consumer allowed one job ran [${ran.join(', ')}]`,
      );
      unwrap(await clock.sweep(minute(3)));
      const rest = bus.sent[2];
      assert(
        rest !== undefined &&
          JSON.stringify(namedJobs(rest)) ===
            JSON.stringify([jobId('lg-1', 'season.open', draftAt)]),
        `the next sweep sent [${rest?.jobIds.join(', ') ?? 'nothing'}], expected the opening alone`,
      );
      unwrap(await clock.consume(work, minute(3)));
      unwrap(await clock.consume(rest, minute(3)));
      assert(
        JSON.stringify(ran) === JSON.stringify(['draft.start', 'season.open']),
        `the first message delivered again, then the second: [${ran.join(', ')}], expected each job once`,
      );
    },
  );

  await check(
    46,
    "an instant a league's rows decide is run once the league is planned again — a request's replan, not the next day's — and a league nobody replanned waits",
    async () => {
      // A trade offer sets its answer-by half an hour out. The request that
      // wrote it asks for the league to be planned again, and the sweep after
      // runs the job at its instant. A league whose rows changed with no
      // replan asked for is planned at its daily instant, which is hours away.
      const leagues = [leagueFor('lg-1', settingsFor({})), leagueFor('lg-2', settingsFor({}))];
      // After the daily planning instant, so the next is nearly a day off.
      const start = new Date(
        dailyPlanAt('lg-1', new Date('2026-09-20T00:00:00.000Z')).getTime() + 60 * 60_000,
      );
      const answerBy = start.getTime() + 30 * 60_000;
      let open = false;
      const ran: string[] = [];
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'trades.advance',
          {
            kind: 'trades.advance',
            instants: async () => (open ? [answerBy] : []),
            run: async ({ job }) => {
              ran.push(`${job.leagueId} ${job.dueAt}`);
              return { status: 'ok' };
            },
          },
        ],
      ]);
      const clock = await make(worldFor(leagues, threeWeeks(), { handlers }));
      unwrap(await clock.sweep(start));
      open = true;
      // What the request's row does at the next minute, for one league.
      unwrap(await clock.plan('lg-1', new Date(start.getTime() + 60_000)));
      unwrap(await clock.sweep(new Date(answerBy + 60_000)));
      const want = [`lg-1 ${new Date(answerBy).toISOString()}`];
      assert(
        JSON.stringify(ran) === JSON.stringify(want) &&
          dailyPlanAt('lg-2', start).getTime() > answerBy + 60_000,
        `after the replan the sweep ran [${ran.join(', ')}], expected [${want.join(', ')}] and nothing for the league nobody replanned`,
      );
    },
  );

  await check(
    47,
    'a job that did something, of a kind that can move what the planner reads, has its league planned again in the same minute: the calendar a draft writes has its first period opened before the sweep returns',
    async () => {
      // A league made through the form has no calendar until its draft
      // writes one (`withCalendar`). The opening of its first period, due
      // days before, is run by the same sweep as the draft: the draft's `ok`
      // asks for a replan, the replan writes the period's jobs, and the
      // sweep, its own consumer, runs them. A planner that waited for the
      // daily run would open the week hours late.
      const draftAt = '2026-09-10T20:00:00.000Z';
      const league = leagueFor('lg-1', settingsFor({ draftScheduledAt: draftAt }), 'setup');
      let calendar: ClockService | null = null;
      const ran: string[] = [];
      const handlers = new Map<JobKindId, JobHandler>([
        [
          'draft.start',
          withCalendar(
            {
              kind: 'draft.start',
              run: async () => {
                ran.push('draft.start');
                return { status: 'ok' };
              },
            },
            {
              materialize: (id, now) => calendar!.materialize(id, now),
              forLeague: (id) => calendar!.forLeague(id),
            },
          ),
        ],
        [
          'period.open',
          {
            kind: 'period.open',
            run: async ({ job }) => {
              ran.push(`period.open ${job.period ?? '-'}`);
              return { status: 'ok' };
            },
          },
        ],
      ]);
      const clock = await make(worldFor([league], threeWeeks(), { handlers }));
      calendar = clock;
      unwrap(await clock.sweep(new Date(Date.parse(draftAt) + 60_000)));
      assert(
        JSON.stringify(ran) === JSON.stringify(['draft.start', 'period.open 1']),
        `the sweep at the draft ran [${ran.join(', ')}], expected the draft and then the first period's opening`,
      );
    },
  );

  await check(
    49,
    'a league held still is never planned and nothing runs in it, while the league beside it plays its week',
    async () => {
      // THE DEMO LEAGUE (O6) stands at a Tuesday morning in a database whose
      // clock keeps going. Its calendar is real, so without the hold the
      // planner would lock its next lineups and score its next weeks.
      const held = '2026-09-15T14:00:00.000Z';
      const leagues = [
        leagueFor('lg-1', settingsFor({})),
        leagueFor('lg-2', settingsFor({}), 'in-season', null, held),
      ];
      const ran: string[] = [];
      const recorder = (kind: JobKindId): [JobKindId, JobHandler] => [
        kind,
        {
          kind,
          run: async ({ job }) => {
            ran.push(`${job.leagueId} ${kind}`);
            return { status: 'ok' };
          },
        },
      ];
      const handlers = new Map<JobKindId, JobHandler>([
        recorder('period.open'),
        recorder('lineup.lock'),
        recorder('matchup.score'),
        recorder('matchup.finalize'),
      ]);
      const clock = await make(worldFor(leagues, threeWeeks(), { handlers }));
      unwrap(await clock.materialize('lg-1', NOW));
      unwrap(await clock.materialize('lg-2', NOW));
      const report = unwrap(await clock.plan('lg-2', NOW));
      assert(
        report.held && report.planned === 0 && !report.kept,
        `planning the held league answered ${JSON.stringify(report)}, expected nothing planned and the league out of the clock`,
      );
      unwrap(await clock.sweep(new Date('2026-09-16T12:00:00.000Z')));
      assert(
        ran.some((r) => r.startsWith('lg-1 ')),
        'the league that keeps time ran nothing in its first week',
      );
      assert(
        !ran.some((r) => r.startsWith('lg-2 ')),
        `the held league ran [${ran.filter((r) => r.startsWith('lg-2 ')).join(', ')}]`,
      );
      // A ROW THAT REACHES IT ANYWAY, written before it was held, is settled
      // without its handler running.
      const dueAt = '2026-09-15T00:00:00.000Z';
      const outcome = unwrap(
        await clock.run(
          {
            id: jobId('lg-2', 'matchup.score', dueAt),
            leagueId: 'lg-2',
            kind: 'matchup.score',
            dueAt,
            period: 1,
            windowId: null,
          },
          new Date(held),
        ),
      );
      assert(
        outcome.status === 'idle' && !ran.some((r) => r.startsWith('lg-2 ')),
        `a score job for the held league came back ${outcome.status} and ran [${ran.join(', ')}]`,
      );
    },
  );

  await check(
    50,
    "a job whose kind writes nothing the planner reads — a lock, a score, a notice, a bot's lineup, the matchup cue — does not have its league planned again",
    async () => {
      // The kinds of `PLANS_NOTHING` a week plans here — its locks and its
      // lineup notices — do something (`ok`); the week's opening, which can
      // move the planner's answer, finds nothing to do. The
      // message they arrive in is consumed, and the minute after sends nothing:
      // no replan was asked for. Check 42 is the other half, an `ok` opening's.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      const did = (kind: JobKindId, status: 'ok' | 'idle'): [JobKindId, JobHandler] => [
        kind,
        { kind, run: async () => ({ status }) },
      ];
      const handlers = new Map<JobKindId, JobHandler>([
        ...[...PLANS_NOTHING].map((kind) => did(kind, 'ok')),
        did('period.open', 'idle'),
      ]);
      const bus = recordingBus();
      const clock = await make(worldFor([league], threeWeeks(), { handlers, bus }));
      unwrap(await clock.materialize('lg-1', NOW));
      const at = new Date('2026-09-12T00:00:00.000Z');
      unwrap(await clock.sweep(at));
      unwrap(await clock.consume(bus.sent[0]!, at));
      const second = new Date(at.getTime() + 60_000);
      unwrap(await clock.sweep(second));
      const named = sentCount(bus) === 2 ? namedJobs(bus.sent[1]!) : [];
      assert(
        named.some((id) => id.includes('|lineup.lock|')),
        `the second sweep sent [${named.join(', ')}], expected the week's locks among them`,
      );
      const outcomes = unwrap(await clock.consume(bus.sent[1]!, second));
      assert(
        outcomes.some((o) => o.status === 'ok'),
        `nothing in the message did anything: ${JSON.stringify(outcomes.map((o) => o.status))}`,
      );
      unwrap(await clock.sweep(new Date(second.getTime() + 60_000)));
      assert(
        sentCount(bus) === 2,
        `the minute after sent [${bus.sent
          .slice(2)
          .map((m) => m.jobIds.join(' '))
          .join(' | ')}], expected nothing: no job in the message asked for a replan`,
      );
    },
  );
  await check(
    51,
    'a game moved once a league has its calendar is in the calendar after its next planning run: the week not yet played follows it, a played week does not, and an unchanged schedule changes nothing',
    async () => {
      // Scenario N4. A week played, then the league office moves two of the
      // next week's games: Thursday's to the Tuesday after Monday night, as
      // games were in 2020, and Sunday's first to the same Tuesday. The
      // week's first kickoff is now Sunday's late game and its end the
      // Wednesday morning after, which only a calendar built again can say.
      const league = leagueFor('lg-1', settingsFor({ lockRule: 'first-game' }));
      const world = worldFor([league], threeWeeks());
      const clock = await make(world);
      unwrap(await clock.materialize('lg-1', NOW));
      const before = unwrap(await clock.forLeague('lg-1'));
      const wasWeek1 = JSON.stringify(before.find((p) => p.index === 1));
      const wasEnd = before.find((p) => p.index === 2)!.end;

      const at = new Date('2026-09-16T12:00:00.000Z');
      const tuesday = '2026-09-22T23:00:00.000Z';
      // AND WEEK 1'S MONDAY GAME RE-TIMED AFTER THE FACT, which would move the
      // played week's end if anything were allowed to.
      const moved = world.games.map((g) =>
        g.id === 'g-w2-0' || g.id === 'g-w2-1'
          ? { ...g, start: tuesday }
          : g.id === 'g-w1-3'
            ? { ...g, start: '2026-09-15T01:00:00.000Z' }
            : g,
      );
      world.games.splice(0, world.games.length, ...moved);
      await world.onGamesChanged?.();

      unwrap(await clock.plan('lg-1', at));
      const after = unwrap(await clock.forLeague('lg-1'));
      assert(
        JSON.stringify(after.find((p) => p.index === 1)) === wasWeek1,
        'the week already played moved',
      );
      const week2 = after.find((p) => p.index === 2)!;
      assert(
        week2.start === before.find((p) => p.index === 1)!.end,
        `week 2 starts ${week2.start}, not where the played week ended`,
      );
      assert(
        week2.end === '2026-09-23T11:00:00.000Z',
        `week 2 ends ${week2.end}, where it ended ${wasEnd}: the calendar did not follow the Tuesday game`,
      );
      assert(
        week2.windows[0]!.firstKickoff === '2026-09-20T20:25:00.000Z',
        `week 2's first kickoff reads ${week2.windows[0]!.firstKickoff}`,
      );
      const lock = unwrap(await clock.stateOf('lg-1', new Date('2026-09-19T00:00:00.000Z'))).current
        ?.windows[0]?.lockAt;
      assert(
        lock === '2026-09-20T20:25:00.000Z',
        `week 2 locks at ${lock}, not at its first game as moved`,
      );

      // Planned again with nothing moved, it writes nothing new.
      const settled = JSON.stringify(after);
      unwrap(await clock.plan('lg-1', new Date(at.getTime() + 60_000)));
      assert(
        JSON.stringify(unwrap(await clock.forLeague('lg-1'))) === settled,
        'an unchanged schedule rewrote the calendar',
      );
    },
  );
  return results;
}

/** A one-line summary for a script that runs this. */

export function summarize(results: readonly CheckResult[]): string {
  const passed = results.filter((r) => r.ok).length;
  return `${passed}/${results.length} checks passed.`;
}

export function conformanceFailures(results: readonly CheckResult[]): CheckResult[] {
  return results.filter((r) => !r.ok);
}

export { standardHandlers };
